/**
 * Multi-series polyline chart — ported from the design prototype
 * (business-report.js `simChart()`), for the growth simulator: spend and
 * revenue share one scale, ROAS rides its own, faint horizontal grid lines,
 * mono x-axis row. Replaces the dual-axis Chart.js `<Line>`.
 */

export interface MultiLineSeries {
  points: number[];
  color: string;
  /** Series sharing an axis id share a max — the prototype's usd vs ratio. */
  axis?: string;
}

const W = 1000;
const H = 300;
const PL = 8;
const PB = 26;

export default function MultiLine({
  series,
  xLabels,
  height = 280,
}: {
  series: MultiLineSeries[];
  xLabels: string[];
  height?: number;
}) {
  const n = xLabels.length;
  if (n < 2 || series.length === 0) return null;

  const axisMax = new Map<string, number>();
  for (const s of series) {
    const axis = s.axis ?? 'default';
    const max = Math.max(...s.points) * 1.12 || 1;
    axisMax.set(axis, Math.max(axisMax.get(axis) ?? 0, max));
  }

  const x = (i: number) => PL + (i * (W - PL * 2)) / (n - 1);
  const y = (v: number, axis: string) =>
    H - PB - (v / (axisMax.get(axis) ?? 1)) * (H - PB - 10);

  return (
    <div role="img" aria-label="Multi-series line chart">
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="block w-full" style={{ height }}>
        {[0, 0.25, 0.5, 0.75, 1].map((t) => {
          const gy = ((H - PB) * (1 - t) + 6 * t).toFixed(1);
          return (
            <line
              key={t}
              x1={PL}
              x2={W - PL}
              y1={gy}
              y2={gy}
              stroke="var(--chart-grid)"
              strokeWidth="1"
              vectorEffect="non-scaling-stroke"
            />
          );
        })}
        {series.map((s, si) => (
          <polyline
            key={si}
            points={s.points
              .slice(0, n)
              .map((v, i) => `${x(i).toFixed(1)},${y(v, s.axis ?? 'default').toFixed(1)}`)
              .join(' ')}
            fill="none"
            stroke={s.color}
            strokeWidth="2"
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
          />
        ))}
      </svg>
      <div className="grid grid-flow-col auto-cols-fr pt-1.5 font-mono text-[11px] text-muted-foreground/80">
        {xLabels.map((l, i) => (
          <span
            key={`${l}-${i}`}
            className={i === 0 ? 'text-left' : i === n - 1 ? 'text-right' : 'text-center'}
          >
            {l}
          </span>
        ))}
      </div>
    </div>
  );
}
